import { registerPwa } from './lib/pwa';
import { serializeJson } from './lib/serializeJson';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { dehydrate, hydrate, QueryClient } from '@tanstack/react-query';

// Self-hosted Inter (variable). Vite bundles the woff2 into /assets, so it is
// served from our own origin and the §10.5 `font-src 'self'` CSP holds.
import '@fontsource-variable/inter';
import { Root } from './Root';
import { ApiError } from './lib/api';
import './styles/index.css';
import { startReleaseWatcher } from './lib/releaseWatcher';

// Detect new deploys and hard-reload open tabs so nobody keeps a stale bundle (§9.3).
startReleaseWatcher();

// SRS §8.1 — TanStack Query is the response cache layer.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      // §10.2: bounded query-only retries; never repeat a checkout POST automatically.
      retry: (count, error) =>
        count < 1 && (!(error instanceof ApiError) || error.status === 429 || error.status >= 500),
      retryDelay: (_attempt, error) =>
        error instanceof ApiError && error.retryAfterSeconds !== undefined
          ? error.retryAfterSeconds * 1000
          : 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Appendix A.7: public-only query data keeps optional HTML snapshots from flashing loaders.
const snapshot = document.getElementById('skytech-query-state');
let validSnapshot = false;
if (snapshot?.textContent) {
  try {
    hydrate(queryClient, JSON.parse(snapshot.textContent));
    validSnapshot = true;
  } catch {
    /* Fall back to live queries. */
  }
}
document.addEventListener('skytech-prerender', async () => {
  // Finish loading the renderer BEFORE capturing cache state, so pending queries
  // cannot settle between serialization and the synchronous render.
  const { renderSnapshot } = await import('./snapshot');
  const state = dehydrate(queryClient, {
    shouldDehydrateQuery: (query) =>
      query.state.status === 'success' &&
      ['products', 'categories', 'banners', 'pickup-locations', 'delivery-zones'].includes(
        String(query.queryKey[0]),
      ),
  });
  let node = document.getElementById('skytech-query-state');
  if (!node) {
    node = document.createElement('script');
    node.id = 'skytech-query-state';
    node.setAttribute('type', 'application/json');
    document.head.append(node);
  }
  node.textContent = serializeJson(state);
  const root = document.getElementById('root');
  if (root) root.innerHTML = renderSnapshot(queryClient);
  document.documentElement.dataset.snapshotReady = 'true';
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

if (validSnapshot && container.hasChildNodes()) {
  hydrateRoot(container, <Root client={queryClient} />);
} else {
  createRoot(container).render(<Root client={queryClient} />);
}

registerPwa();
